import { ApiError, addCustomHostname, getCustomHostname, removeCustomHostname } from '@/lib/api';
import type { CustomHostnameState } from '@/lib/types';
import { AlertCircle, CheckCircle2, Globe } from 'lucide-react';
import { useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function redirectDeadline(removedAt: string): string {
  const d = new Date(new Date(removedAt).getTime() + 30 * 24 * 60 * 60 * 1000);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function cnameTarget(hostname: string): string {
  // Return the subdomain part only — e.g. "book" from "book.example.com"
  const parts = hostname.split('.');
  return parts.length > 2 ? (parts[0] ?? hostname) : hostname;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function AddHostnameForm({
  onAdded,
}: {
  onAdded: (state: CustomHostnameState) => void;
}) {
  const [hostname, setHostname] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = hostname.trim();
    if (!trimmed) return;
    setSaving(true);
    setError(null);
    try {
      const state = await addCustomHostname(trimmed);
      onAdded(state);
    } catch (err) {
      const msg = err instanceof ApiError ? err.code : 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div>
        <label
          htmlFor="custom-hostname"
          className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
        >
          Custom domain
        </label>
        <input
          id="custom-hostname"
          type="text"
          value={hostname}
          onChange={(e) => setHostname(e.target.value)}
          placeholder="book.yourkennel.com"
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          disabled={saving}
        />
        <p className="mt-1 text-xs text-muted-foreground">
          Enter the hostname you want customers to use, e.g.{' '}
          <span className="font-mono">book.yourkennel.com</span>.
        </p>
      </div>
      <button
        type="submit"
        disabled={saving || hostname.trim().length === 0}
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press disabled:opacity-50"
      >
        {saving ? 'Adding…' : 'Add custom domain'}
      </button>
    </form>
  );
}

function RemoveButton({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);

  async function handleRemove() {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setRemoving(true);
    onRemove();
  }

  return (
    <button
      type="button"
      onClick={() => void handleRemove()}
      disabled={removing}
      className="rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:text-destructive hover:border-destructive press disabled:opacity-50"
    >
      {confirming ? 'Confirm removal' : label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function CustomDomainSettings() {
  const [state, setState] = useState<CustomHostnameState | null>(null);
  const [loading, setLoading] = useState(true);

  function load() {
    setLoading(true);
    getCustomHostname()
      .then((s) => setState(s))
      .catch(() => {
        // Non-fatal — leave state null
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
  }, []);

  async function handleRemove() {
    try {
      await removeCustomHostname();
      load();
    } catch {
      // Non-fatal — refetch to show current state
      load();
    }
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-[720px] px-6 py-10 md:px-10 md:py-14">
        <div className="space-y-3">
          <div className="h-8 w-48 animate-pulse rounded bg-secondary" />
          <div className="h-4 w-full animate-pulse rounded bg-secondary" />
          <div className="h-4 w-3/4 animate-pulse rounded bg-secondary" />
        </div>
      </div>
    );
  }

  const status = state?.status ?? null;
  const hostname = state?.hostname ?? null;

  // Determine if within 30-day redirect window for 'removed' status
  const withinRedirectWindow =
    status === 'removed' &&
    state?.removedAt != null &&
    Date.now() - new Date(state.removedAt).getTime() < 30 * 24 * 60 * 60 * 1000;

  return (
    <div className="space-y-8">
      <header>
        <h1 className="font-heading text-2xl font-medium tracking-tight">Custom domain</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Serve your customer portal from your own domain instead of{' '}
          <span className="font-mono">{'{slug}'}.kennel.app</span>.
        </p>
      </header>

      <div className="space-y-6">
        {/* No hostname set or removed past 30d */}
        {(status === null || (status === 'removed' && !withinRedirectWindow)) && (
          <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
            <div className="border-b border-border px-6 py-4">
              <h2 className="flex items-center gap-2 text-sm font-medium">
                <Globe className="size-4 text-muted-foreground" strokeWidth={1.75} />
                Add custom domain
              </h2>
            </div>
            <div className="px-6 py-5">
              <AddHostnameForm onAdded={(s) => setState(s)} />
            </div>
          </section>
        )}

        {/* Removed within 30d — show redirect notice + form to add new */}
        {status === 'removed' && withinRedirectWindow && state?.removedAt && (
          <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
            <div className="border-b border-border px-6 py-4">
              <h2 className="flex items-center gap-2 text-sm font-medium">
                <Globe className="size-4 text-muted-foreground" strokeWidth={1.75} />
                Domain removed
              </h2>
            </div>
            <div className="px-6 py-5 space-y-5">
              <p className="text-sm text-muted-foreground">
                Old links to{' '}
                <span className="font-mono font-medium text-foreground">{hostname}</span> will
                redirect to your subdomain until{' '}
                <span className="font-medium text-foreground">
                  {redirectDeadline(state.removedAt)}
                </span>
                .
              </p>
              <AddHostnameForm onAdded={(s) => setState(s)} />
            </div>
          </section>
        )}

        {/* pending_dns — CNAME instructions */}
        {status === 'pending_dns' && hostname && (
          <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
            <div className="border-b border-border px-6 py-4">
              <h2 className="flex items-center gap-2 text-sm font-medium">
                <Globe className="size-4 text-warning" strokeWidth={1.75} />
                Waiting for DNS
              </h2>
            </div>
            <div className="px-6 py-5 space-y-5">
              <p className="text-sm text-muted-foreground">
                Add the following CNAME record to your DNS provider, then click "Check now".
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-[480px] w-full text-sm font-mono border border-border rounded-md overflow-hidden">
                  <thead>
                    <tr className="bg-secondary text-muted-foreground text-[11px] uppercase tracking-wide">
                      <th className="px-4 py-2 text-left font-medium">Type</th>
                      <th className="px-4 py-2 text-left font-medium">Name</th>
                      <th className="px-4 py-2 text-left font-medium">Target</th>
                      <th className="px-4 py-2 text-left font-medium">TTL</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-border">
                      <td className="px-4 py-2">CNAME</td>
                      <td className="px-4 py-2">{cnameTarget(hostname)}</td>
                      <td className="px-4 py-2">proxy.kennel.app</td>
                      <td className="px-4 py-2 num">300</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={load}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press"
                >
                  I added the record — check now
                </button>
                <RemoveButton label="Remove and start over" onRemove={() => void handleRemove()} />
              </div>
            </div>
          </section>
        )}

        {/* pending_ssl — DNS verified, SSL provisioning */}
        {status === 'pending_ssl' && hostname && (
          <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
            <div className="border-b border-border px-6 py-4">
              <h2 className="flex items-center gap-2 text-sm font-medium">
                <Globe className="size-4 text-warning" strokeWidth={1.75} />
                Provisioning SSL certificate
              </h2>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-muted-foreground">
                DNS verified for{' '}
                <span className="font-mono font-medium text-foreground">{hostname}</span>. SSL
                certificate is being provisioned — this usually takes a few minutes.
              </p>
              <RemoveButton label="Remove" onRemove={() => void handleRemove()} />
            </div>
          </section>
        )}

        {/* active */}
        {status === 'active' && hostname && (
          <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
            <div className="border-b border-border px-6 py-4">
              <h2 className="flex items-center gap-2 text-sm font-medium">
                <CheckCircle2 className="size-4 text-success" strokeWidth={1.75} />
                Custom domain active
              </h2>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-muted-foreground">
                Your customer portal is live at{' '}
                <a
                  href={`https://${hostname}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-mono font-medium text-primary underline-offset-4 hover:underline"
                >
                  {hostname}
                </a>
                .
              </p>
              <RemoveButton label="Remove custom domain" onRemove={() => void handleRemove()} />
            </div>
          </section>
        )}

        {/* failed */}
        {status === 'failed' && (
          <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
            <div className="border-b border-border px-6 py-4">
              <h2 className="flex items-center gap-2 text-sm font-medium">
                <AlertCircle className="size-4 text-destructive" strokeWidth={1.75} />
                Custom domain setup failed
              </h2>
            </div>
            <div className="px-6 py-5 space-y-4">
              {state?.failureReason && (
                <p className="text-sm text-destructive">{state.failureReason}</p>
              )}
              <RemoveButton label="Remove and try again" onRemove={() => void handleRemove()} />
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
